import { AddCommentOutlined, EditOutlined } from "@mui/icons-material";
import { Box, ClickAwayListener, Paper, Popper } from "@mui/material";
import { useMemo } from "react";
import {
  getDynamicNodeEditLabel,
  isEditableDynamicNodeName,
} from "src/tiptap/components/tiptap-node/dynamic-nodes/dynamic-node-selection";
import { Button } from "src/tiptap/components/tiptap-ui-primitive/button";
import {
  Toolbar,
  ToolbarGroup,
} from "src/tiptap/components/tiptap-ui-primitive/toolbar";
import { CommentInput } from "./CommentInput";
import type { TiptapCommentsController } from "./types";

type TiptapSelectionToolbarProps = {
  controller: TiptapCommentsController;
  onEditSelection?: () => void;
};

const getVirtualAnchor = (anchorRect: DOMRect) => ({
  getBoundingClientRect: () =>
    new DOMRect(anchorRect.left, anchorRect.bottom, 0, 0),
});

export const TiptapSelectionToolbar = ({
  controller,
  onEditSelection,
}: TiptapSelectionToolbarProps) => {
  const selection = controller.selection;
  const anchorEl = useMemo(
    () =>
      selection?.anchorRect ? getVirtualAnchor(selection.anchorRect) : null,
    [selection?.anchorRect]
  );
  const editLabel = getDynamicNodeEditLabel(selection?.nodeType);

  if (!selection || !anchorEl || controller.disabled) {
    return null;
  }

  return (
    <>
      <Popper
        open={!controller.isNewThreadInputOpen && controller.canCreateThread}
        container={document.getElementById("root")}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 4],
            },
          },
          {
            name: "preventOverflow",
            options: {
              boundary: "viewport",
              padding: 8,
            },
          },
          {
            name: "flip",
            options: {
              fallbackPlacements: ["top-start", "bottom-end", "top-end"],
            },
          },
        ]}
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener
          mouseEvent="onMouseDown"
          onClickAway={(event) => {
            if (
              event.target instanceof Node &&
              controller.editor?.view.dom.contains(event.target)
            ) {
              return;
            }
            controller.clearSelection();
          }}
        >
          <Toolbar
            variant="floating"
            data-testid="tiptap-selection-floating-toolbar"
          >
            <ToolbarGroup>
              <Button
                type="button"
                variant="ghost"
                tooltip="Add comment"
                aria-label="Add comment"
                data-testid="tiptap-toolbar-add-comment"
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  controller.openNewThreadInput();
                }}
              >
                <AddCommentOutlined className="tiptap-button-icon" />
              </Button>
              {onEditSelection &&
              isEditableDynamicNodeName(selection.nodeType) ? (
                <Button
                  type="button"
                  variant="ghost"
                  tooltip={editLabel}
                  aria-label={editLabel}
                  onMouseDown={(event) => {
                    event.preventDefault();
                  }}
                  onClick={(event) => {
                    event.preventDefault();
                    onEditSelection();
                  }}
                >
                  <EditOutlined className="tiptap-button-icon" />
                </Button>
              ) : null}
            </ToolbarGroup>
          </Toolbar>
        </ClickAwayListener>
      </Popper>

      <Popper
        open={controller.isNewThreadInputOpen}
        container={document.getElementById("root")}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 4],
            },
          },
          {
            name: "preventOverflow",
            options: {
              boundary: "viewport",
              padding: 8,
            },
          },
          {
            name: "flip",
            options: {
              fallbackPlacements: ["top-start", "bottom-end", "top-end"],
            },
          },
        ]}
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={controller.clearSelection}>
          <Paper
            elevation={8}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            sx={{
              minWidth: 320,
              maxWidth: 420,
              borderRadius: "8px",
              overflow: "visible",
            }}
          >
            <Box sx={{ p: 2 }}>
              <CommentInput
                onSubmit={(content) => {
                  controller.createThread(content);
                }}
                onCancel={controller.clearSelection}
                placeholder="Write your comment... Use @ to mention"
                submitLabel="Submit"
                autoFocus
                orgUsers={controller.orgUsers}
                showActionsOnFocusOnly
              />
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
